import { ClubMark } from "@/components/ClubMark";
import { getSideLogos } from "@/lib/matchLogos";
import type { FeedbackMatch, Match } from "@/lib/types";

/** The same home/away crest preview in the coach library and family cards. */
export function MatchRecapThumbnail({
  match,
  clubLogoUrl,
  tenantTeamName,
  size = "library",
  className = "",
  testId = "match-recap-thumbnail",
}: {
  match: Match | FeedbackMatch | null | undefined;
  clubLogoUrl: string | null | undefined;
  tenantTeamName?: string | null;
  size?: "library" | "card";
  className?: string;
  testId?: string;
}) {
  const label = match && "label" in match ? match.label : "";
  const sides = label.split(/\s+vs\.?\s+/i);
  const homeName = match?.home_team?.trim() || sides[0]?.trim() || "Home team";
  const awayName = match?.away_team?.trim() || sides[1]?.split(" — ")[0]?.trim() || "Away team";
  const { homeUrl, awayUrl } = getSideLogos(match, clubLogoUrl, tenantTeamName);
  const logoSize = size === "card"
    ? "!h-[72px] !w-[55px]"
    : "!h-9 !w-6 sm:!h-11 sm:!w-8";
  const padding = size === "card" ? "p-2" : "p-1 sm:p-2";

  return (
    <span
      role="img"
      aria-label={`${homeName} versus ${awayName} club logos`}
      data-testid={testId}
      className={`match-recap-thumbnail !flex shrink-0 items-center justify-center gap-1 overflow-hidden rounded-inset border border-rule bg-elevated ${padding} ${className}`}
    >
      <span aria-hidden="true">
        <ClubMark
          name={homeName}
          imageUrl={homeUrl}
          initials={2}
          label={`${homeName} logo`}
          className={`recap-club-mark ${logoSize} text-[0.6rem] sm:text-sm`}
        />
      </span>
      <span aria-hidden="true" className="text-[0.65rem] font-display font-semibold text-ink-muted">
        vs
      </span>
      <span aria-hidden="true">
        <ClubMark
          name={awayName}
          imageUrl={awayUrl}
          initials={2}
          label={`${awayName} logo`}
          className={`recap-club-mark ${logoSize} text-[0.6rem] sm:text-sm`}
        />
      </span>
    </span>
  );
}
